import { ManagementViewKpis } from '../../../shared/types/managementViewKpi.ts';
import {
    formatBatchStart,
    formatCycleTime,
    formatDuration,
    formatEfficiency,
    formatEurosPerHour,
    formatPartsPerHour,
    formatPartsProduced,
    formatPricePerPart,
    formatProduct,
    formatQuality,
    formatSetupTime,
} from '../../utils/fieldFormats.ts';


export type ManagementViewColumnId =
    | 'equipment'
    | 'partNumber'
    | 'start'
    | 'setupTime'
    | 'currentCycleTime'
    | 'bestCycleTime'
    | 'intervalTarget'
    | 'intervalTime'
    | 'measurementSequence'
    | 'partsPerHour'
    | 'goodParts'
    | 'totalParts'
    | 'scrapParts'
    | 'productionTime'
    | 'quality'
    | 'availability'
    | 'performance'
    | 'oee'
    | 'efficiencyTotal'
    | 'efficiencyNight'
    | 'efficiencyMorning'
    | 'efficiencyEvening'
    | 'unitPrice'
    | 'eurosPerHour'
    | 'eurosPerHourNight'
    | 'eurosPerHourMorning'
    | 'eurosPerHourEvening';

export interface ManagementViewRow {
    equipmentId: string;
    equipmentName: string;
    isLoading: boolean;
    kpis?: ManagementViewKpis;
}

export interface ManagementViewColumn {
    id: ManagementViewColumnId;
    label: string;
    menuLabel?: string;
    exportLabel?: string;
    title?: string;
    isAdminOnly?: boolean;
    headerStyle?: {
        minWidth?: string;
        maxWidth?: string;
    };
    cellClassName?: string;
    getSortValue: (row: ManagementViewRow) => string | number | Date | null | undefined;
    formatValue: (row: ManagementViewRow) => string;
}

export interface ManagementViewSort {
    columnId: ManagementViewColumnId;
    direction: 'asc' | 'desc';
}

export interface ManagementViewLoadingProgress {
    completed: number;
    total: number;
    percentage: number;
    isLoading: boolean;
}

const SHIFT_EFFICIENCY_TITLE = [
    'efficiency = median cycle time * good parts count / production time',
    'The formula only considers parts in the given shift',
].join('\n');

export const MANAGEMENT_VIEW_COLUMNS: ManagementViewColumn[] = [
    {
        id: 'equipment',
        label: 'Equipment',
        cellClassName: 'equipment',
        getSortValue: row => row.equipmentName,
        formatValue: row => row.equipmentName,
    },
    {
        id: 'partNumber',
        label: 'Part number',
        cellClassName: 'part-number',
        getSortValue: row => row.kpis?.partDisplayName ?? row.kpis?.product,
        formatValue: row => formatProduct(row.kpis?.partDisplayName),
    },
    {
        id: 'start',
        label: 'Start',
        headerStyle: { minWidth: '140px' },
        getSortValue: row => row.kpis?.batchStart,
        formatValue: row => formatBatchStart(row.kpis?.batchStart),
    },
    {
        id: 'setupTime',
        label: 'Setup time',
        getSortValue: row => row.kpis?.setupTime,
        formatValue: row => formatSetupTime(row.kpis?.setupTime),
    },
    {
        id: 'currentCycleTime',
        label: 'Current cycle time',
        title: 'Current batch median cycle time',
        headerStyle: { maxWidth: '100px' },
        getSortValue: row => row.kpis?.currentCycleTime,
        formatValue: row => formatCycleTime(row.kpis?.currentCycleTime),
    },
    {
        id: 'bestCycleTime',
        label: 'Best cycle time',
        title: 'Best median cycle time from all previous runs',
        headerStyle: { maxWidth: '100px' },
        getSortValue: row => row.kpis?.bestCycleTime,
        formatValue: row => formatCycleTime(row.kpis?.bestCycleTime),
    },
    {
        id: 'intervalTarget',
        label: 'Interval target',
        title: 'Measurement interval target count',
        headerStyle: { maxWidth: '100px' },
        getSortValue: row => row.kpis?.intervalTarget,
        formatValue: row => `${row.kpis?.intervalTarget ?? ''}`,
    },
    {
        id: 'intervalTime',
        label: 'Interval time',
        title: 'The time it takes to do a whole measurement interval',
        headerStyle: { maxWidth: '100px' },
        getSortValue: row => row.kpis?.intervalTime,
        formatValue: row => formatCycleTime(row.kpis?.intervalTime),
    },
    {
        id: 'measurementSequence',
        label: 'Measurement sequence',
        title: 'Measurement plan variant sequence',
        headerStyle: { maxWidth: '100px' },
        getSortValue: row => row.kpis?.measurementSequence,
        formatValue: row => row.kpis?.measurementSequence ?? '',
    },
    {
        id: 'partsPerHour',
        label: 'Parts / h',
        title: 'parts/h = (all parts count - scraps) / production time after setup * one hour',
        getSortValue: row => row.kpis?.goodPartsPerHour,
        formatValue: row => formatPartsPerHour(row.kpis?.goodPartsPerHour),
    },
    {
        id: 'goodParts',
        label: 'Good parts',
        title: 'good parts = actual parts produced - scraps\nLatest 8 hours shown in parentheses.',
        getSortValue: row => row.kpis?.goodPartsProduced,
        formatValue: row => formatPartsProduced(row.kpis?.goodPartsProduced),
    },
    {
        id: 'totalParts',
        label: 'Total parts',
        title: 'All produced parts, including scraps, from the start of the current batch.',
        getSortValue: row => row.kpis?.totalPartsProduced,
        formatValue: row => formatPartsProduced(row.kpis?.totalPartsProduced),
    },
    {
        id: 'scrapParts',
        label: 'Scrap parts',
        title: 'Produced parts marked as scrap from the start of the current batch.',
        getSortValue: row => row.kpis?.scrapPartsProduced,
        formatValue: row => formatPartsProduced(row.kpis?.scrapPartsProduced),
    },
    {
        id: 'productionTime',
        label: 'Production time',
        title: 'Time in OPERATE status after setup, through the latest completed part.',
        getSortValue: row => row.kpis?.productionTime,
        formatValue: row => formatDuration(row.kpis?.productionTime ?? null, true, false),
    },
    {
        id: 'quality',
        label: 'Q',
        menuLabel: 'Quality (Q)',
        exportLabel: 'Quality (Q)',
        title: 'Quality = good parts / total parts.',
        getSortValue: row => row.kpis?.quality,
        formatValue: row => formatQuality(row.kpis?.quality),
    },
    {
        id: 'availability',
        label: 'A',
        menuLabel: 'Availability (A)',
        exportLabel: 'Availability (A)',
        title: 'Availability = production time / connected time after setup.',
        getSortValue: row => row.kpis?.availability,
        formatValue: row => formatEfficiency(row.kpis?.availability),
    },
    {
        id: 'performance',
        label: 'P',
        menuLabel: 'Performance (P)',
        exportLabel: 'Performance (P)',
        title: 'Performance = total parts * best historical cycle time / production time.',
        getSortValue: row => row.kpis?.performance,
        formatValue: row => formatEfficiency(row.kpis?.performance),
    },
    {
        id: 'oee',
        label: 'OEE',
        title: 'OEE = Availability * Performance * Quality.',
        getSortValue: row => row.kpis?.oee,
        formatValue: row => formatEfficiency(row.kpis?.oee),
    },
    {
        id: 'efficiencyTotal',
        label: 'Efficiency from start',
        title: 'efficiency = median cycle time * good parts count / production time',
        headerStyle: { maxWidth: '100px' },
        getSortValue: row => row.kpis?.efficiency,
        formatValue: row => formatEfficiency(row.kpis?.efficiency),
    },
    {
        id: 'efficiencyNight',
        label: 'Y\u00f6',
        menuLabel: 'Efficiency - Y\u00f6',
        exportLabel: 'Efficiency - Y\u00f6',
        title: SHIFT_EFFICIENCY_TITLE,
        getSortValue: row => row.kpis?.efficiencyNight,
        formatValue: row => formatEfficiency(row.kpis?.efficiencyNight),
    },
    {
        id: 'efficiencyMorning',
        label: 'Aamu',
        menuLabel: 'Efficiency - Aamu',
        exportLabel: 'Efficiency - Aamu',
        title: SHIFT_EFFICIENCY_TITLE,
        getSortValue: row => row.kpis?.efficiencyMorning,
        formatValue: row => formatEfficiency(row.kpis?.efficiencyMorning),
    },
    {
        id: 'efficiencyEvening',
        label: 'Ilta',
        menuLabel: 'Efficiency - Ilta',
        exportLabel: 'Efficiency - Ilta',
        title: SHIFT_EFFICIENCY_TITLE,
        getSortValue: row => row.kpis?.efficiencyEvening,
        formatValue: row => formatEfficiency(row.kpis?.efficiencyEvening),
    },
    {
        id: 'unitPrice',
        label: 'Unit price',
        isAdminOnly: true,
        cellClassName: 'editable',
        getSortValue: row => row.kpis?.pricePerPart,
        formatValue: row => formatPricePerPart(row.kpis?.pricePerPart),
    },
    {
        id: 'eurosPerHour',
        label: '\u20ac / h',
        menuLabel: '\u20ac / h - From start',
        exportLabel: '\u20ac / h - From start',
        isAdminOnly: true,
        getSortValue: row => row.kpis?.eurosPerHour,
        formatValue: row => formatEurosPerHour(row.kpis?.eurosPerHour),
    },
    {
        id: 'eurosPerHourNight',
        label: 'Y\u00f6',
        menuLabel: '\u20ac / h - Y\u00f6',
        exportLabel: '\u20ac / h - Y\u00f6',
        isAdminOnly: true,
        getSortValue: row => row.kpis?.eurosPerHourNight,
        formatValue: row => formatEurosPerHour(row.kpis?.eurosPerHourNight),
    },
    {
        id: 'eurosPerHourMorning',
        label: 'Aamu',
        menuLabel: '\u20ac / h - Aamu',
        exportLabel: '\u20ac / h - Aamu',
        isAdminOnly: true,
        getSortValue: row => row.kpis?.eurosPerHourMorning,
        formatValue: row => formatEurosPerHour(row.kpis?.eurosPerHourMorning),
    },
    {
        id: 'eurosPerHourEvening',
        label: 'Ilta',
        menuLabel: '\u20ac / h - Ilta',
        exportLabel: '\u20ac / h - Ilta',
        isAdminOnly: true,
        getSortValue: row => row.kpis?.eurosPerHourEvening,
        formatValue: row => formatEurosPerHour(row.kpis?.eurosPerHourEvening),
    },
];

export const MANAGEMENT_VIEW_COLUMN_IDS = MANAGEMENT_VIEW_COLUMNS.map(column => column.id);

// Keep new columns opt-in by adding them to MANAGEMENT_VIEW_COLUMNS but not this list.
export const MANAGEMENT_VIEW_DEFAULT_COLUMN_IDS: ManagementViewColumnId[] = [
    'equipment',
    'partNumber',
    'start',
    'setupTime',
    'currentCycleTime',
    'bestCycleTime',
    'partsPerHour',
    'goodParts',
    'efficiencyTotal',
    'efficiencyNight',
    'efficiencyMorning',
    'efficiencyEvening',
    'unitPrice',
    'eurosPerHour',
    'eurosPerHourNight',
    'eurosPerHourMorning',
    'eurosPerHourEvening',
];

export function normalizeManagementViewColumnIds(
    columnIds: ManagementViewColumnId[],
): ManagementViewColumnId[] {
    return MANAGEMENT_VIEW_COLUMN_IDS.filter(columnId =>
        columnId === 'equipment' || columnIds.includes(columnId)
    );
}

export function getAvailableManagementViewColumns(isAdmin: boolean): ManagementViewColumn[] {
    return MANAGEMENT_VIEW_COLUMNS.filter(column => isAdmin || !column.isAdminOnly);
}

export function getVisibleManagementViewColumns(
    visibleColumnIds: ManagementViewColumnId[],
    isAdmin: boolean,
): ManagementViewColumn[] {
    const visibleColumnIdSet = new Set(visibleColumnIds);
    return getAvailableManagementViewColumns(isAdmin)
        .filter(column => visibleColumnIdSet.has(column.id));
}

export function getManagementViewLoadingProgress(
    rows: ManagementViewRow[],
): ManagementViewLoadingProgress {
    const total = rows.length;
    const completed = rows.filter(row => !row.isLoading).length;

    return {
        completed,
        total,
        percentage: total === 0 ? 0 : Math.round(completed / total * 100),
        isLoading: completed < total,
    };
}

export function sortManagementViewRows(
    rows: ManagementViewRow[],
    sort: ManagementViewSort,
): ManagementViewRow[] {
    const column = MANAGEMENT_VIEW_COLUMNS.find(it => it.id === sort.columnId);
    if (!column) return rows;

    return rows.map((row, index) => ({ row, index }))
        .sort((a, b) => {
            const aValue = column.getSortValue(a.row);
            const bValue = column.getSortValue(b.row);
            const valueComparison = compareSortValues(aValue, bValue);

            if (valueComparison != null && valueComparison !== 0) {
                return sort.direction === 'asc' ? valueComparison : -valueComparison;
            }
            if (valueComparison == null) {
                return aValue == null ? 1 : -1;
            }

            const equipmentComparison = a.row.equipmentName.localeCompare(b.row.equipmentName);
            return equipmentComparison || a.index - b.index;
        })
        .map(it => it.row);
}

export function createManagementViewExportTable(
    rows: ManagementViewRow[],
    columns: ManagementViewColumn[],
): string[][] {
    return [
        columns.map(column => column.exportLabel ?? column.label),
        ...rows.map(row => columns.map(column => column.formatValue(row))),
    ];
}

function compareSortValues(
    a: string | number | Date | null | undefined,
    b: string | number | Date | null | undefined,
): number | null {
    if (a == null && b == null) return 0;
    if (a == null || b == null) return null;
    if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return a.toString().localeCompare(b.toString(), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
}
